/**
 * MY Register — 주소 요약 표시 (UUID/B15 코드 → 사람이 읽을 수 있는 이름).
 */
import { ref } from 'vue';
import { joinAddressDisplayParts, formatCountryForAddressDisplay } from '@/features/Contact/utils/addressJoinParts';
import {
    MY_MALAYSIA_COUNTRY_CODE,
    preserveLiteralDisplayPart,
    shouldShowMyLocationFields,
} from '@/features/Contact/utils/contactMyLocationFields';
import { entityMyApi } from '@/features/Entity_my/api/entityApi';
import { baseCodeApi } from '@/shared/api/baseCodeApi';

export type MyRegisterLocOption = { TOTAL_CODE: string; CODE_NAME: string };

const UUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PLACEHOLDER = /^(NA|N\/A|N\.A\.|NONE|null|-)$/i;

const countryOptionsRef = ref<MyRegisterLocOption[]>([]);
const stateOptionsRef = ref<MyRegisterLocOption[]>([]);
const cityCacheByState = new Map<string, MyRegisterLocOption[]>();
const postcodeCacheByCity = new Map<string, MyRegisterLocOption[]>();
const cityNameCacheByGuid = new Map<string, string>();
const locCacheVersion = ref(0);
let baseOptionsLoadStarted = false;

function isGuidLike(value: unknown): boolean {
    return UUID_LIKE.test(String(value ?? '').trim());
}

function isNumericPostcodeLike(value: unknown): boolean {
    return /^\d{4,6}$/.test(String(value ?? '').trim());
}

function isB15CountryCode(raw: unknown): boolean {
    return /^B15\d{3}$/i.test(String(raw ?? '').trim());
}

/** B15 국가 TOTAL_CODE → CODE_NAME(대문자). 옵션 미로드/미존재면 '' (원본 코드 노출 금지). */
function resolveB15CountryName(raw: unknown): string {
    const v = String(raw ?? '').trim();
    if (!v) return '';
    if (v === MY_MALAYSIA_COUNTRY_CODE) return 'MALAYSIA';
    const byCode = countryOptionsRef.value.find((o) => o.TOTAL_CODE.toUpperCase() === v.toUpperCase());
    if (byCode?.CODE_NAME) return preserveLiteralDisplayPart(String(byCode.CODE_NAME).trim());
    return '';
}

function sanitizeDisplayPart(value: unknown): string {
    const s = String(value ?? '').trim();
    if (!s || isGuidLike(s) || PLACEHOLDER.test(s)) return '';
    if (s === MY_MALAYSIA_COUNTRY_CODE) return 'MALAYSIA';
    // B15 국가코드는 항상 CODE_NAME으로 변환 — 못 찾으면 원본 코드를 노출하지 않음
    if (isB15CountryCode(s)) return resolveB15CountryName(s);
    return preserveLiteralDisplayPart(s);
}

function pickFirstSanitized(data: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
        const v = sanitizeDisplayPart(data[key]);
        if (v) return v;
    }
    return '';
}

function pickFirstRaw(data: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
        const s = String(data[key] ?? '').trim();
        if (s) return s;
    }
    return '';
}

async function ensureCityNameForGuid(cityGuid: string): Promise<string> {
    const id = String(cityGuid || '').trim();
    if (!id || !isGuidLike(id)) return '';
    if (cityNameCacheByGuid.has(id)) return cityNameCacheByGuid.get(id) || '';

    try {
        const response = await entityMyApi.getMalaysiaCities(undefined, id, false);
        const cityData = (response as any).data?.data || (response as any).data;
        const cityInfo = Array.isArray(cityData) ? cityData[0] : (cityData || {});
        const name = String(cityInfo?.name || '').trim();
        cityNameCacheByGuid.set(id, name);
        if (name) locCacheVersion.value += 1;
        return name;
    } catch (error) {
        console.error('[myRegisterAddressDisplay] Failed to resolve city GUID', { cityGuid: id, error });
        cityNameCacheByGuid.set(id, '');
        return '';
    }
}

function lookupLocName(raw: string, options: MyRegisterLocOption[]): string {
    const v = String(raw ?? '').trim();
    if (!v) return '';
    if (v === MY_MALAYSIA_COUNTRY_CODE) return 'MALAYSIA';
    if (isGuidLike(v)) {
        const cached = cityNameCacheByGuid.get(v);
        if (cached) return preserveLiteralDisplayPart(cached);
        return '';
    }
    const byCode = options.find((o) => o.TOTAL_CODE === v);
    if (byCode?.CODE_NAME) return preserveLiteralDisplayPart(String(byCode.CODE_NAME).trim());
    const byName = options.find(
        (o) =>
            o.CODE_NAME === v ||
            (o.CODE_NAME && o.CODE_NAME.toUpperCase() === v.toUpperCase()),
    );
    if (byName?.CODE_NAME) return preserveLiteralDisplayPart(String(byName.CODE_NAME).trim());
    // B15 국가코드가 잘못된 슬롯(state/city 등)에 들어와도 country 옵션으로 이름 변환
    if (isB15CountryCode(v)) return resolveB15CountryName(v);
    return sanitizeDisplayPart(v);
}

function resolveLocPart(
    data: Record<string, unknown>,
    textKeys: string[],
    codeKeys: string[],
    options: MyRegisterLocOption[],
): string {
    const fromText = pickFirstSanitized(data, textKeys);
    if (fromText) {
        if (isB15CountryCode(fromText) || isGuidLike(fromText)) {
            const resolved = lookupLocName(fromText, options);
            if (resolved) return resolved;
        }
        if (!isB15CountryCode(fromText)) return fromText;
    }
    for (const key of codeKeys) {
        const resolved = lookupLocName(String(data[key] ?? ''), options);
        if (resolved) return resolved;
    }
    return '';
}

async function loadBaseLocationOptions(): Promise<void> {
    try {
        const [countryRes, stateRes] = await Promise.all([
            baseCodeApi.getCodesByMainCode('CM', 'B15', undefined),
            entityMyApi.getMalaysiaStates(),
        ]);

        const codes = (countryRes as any).codes || (countryRes as any).data?.codes || [];
        countryOptionsRef.value = codes
            .map((code: any) => ({
                TOTAL_CODE: String(code.totalCode || code.TOTAL_CODE || '').trim(),
                CODE_NAME: String(
                    code.defaultCodeName ||
                        code.DEFAULT_CODE_NAME ||
                        code.codeName ||
                        code.CODE_NAME ||
                        '',
                )
                    .trim()
                    .toUpperCase(),
            }))
            .filter((item: MyRegisterLocOption) => item.TOTAL_CODE && item.CODE_NAME);

        const states = (stateRes as any).data?.data || (stateRes as any).data || [];
        stateOptionsRef.value = (Array.isArray(states) ? states : [])
            .map((state: any) => ({
                TOTAL_CODE: String(state.code || '').trim(),
                CODE_NAME: String(state.name || '').trim(),
            }))
            .filter((item: MyRegisterLocOption) => item.TOTAL_CODE && item.CODE_NAME);
    } catch (error) {
        console.error('[myRegisterAddressDisplay] Failed to load base location options', error);
    } finally {
        locCacheVersion.value += 1;
    }
}

async function ensureCitiesForState(stateCode: string): Promise<MyRegisterLocOption[]> {
    const code = String(stateCode || '').trim();
    if (!code) return [];
    if (cityCacheByState.has(code)) return cityCacheByState.get(code) || [];

    try {
        const response = await entityMyApi.getMalaysiaCities(code, undefined, false);
        const cities = (response as any).data?.data || (response as any).data || [];
        const mapped = (Array.isArray(cities) ? cities : [])
            .map((city: any) => ({
                TOTAL_CODE: String(city.code || '').trim(),
                CODE_NAME: String(city.name || '').trim(),
            }))
            .filter((item: MyRegisterLocOption) => item.TOTAL_CODE && item.CODE_NAME);
        cityCacheByState.set(code, mapped);
        locCacheVersion.value += 1;
        return mapped;
    } catch (error) {
        console.error('[myRegisterAddressDisplay] Failed to load cities', { stateCode: code, error });
        cityCacheByState.set(code, []);
        return [];
    }
}

async function ensurePostcodesForCity(cityCode: string): Promise<MyRegisterLocOption[]> {
    const code = String(cityCode || '').trim();
    if (!code) return [];
    if (postcodeCacheByCity.has(code)) return postcodeCacheByCity.get(code) || [];

    try {
        const response = await entityMyApi.getMalaysiaPostcodes(code);
        const postcodes = (response as any).data?.data || (response as any).data || [];
        const mapped = (Array.isArray(postcodes) ? postcodes : [])
            .map((postcode: any) => ({
                TOTAL_CODE: String(postcode.code || '').trim(),
                CODE_NAME: String(postcode.name || '').trim(),
            }))
            .filter((item: MyRegisterLocOption) => item.TOTAL_CODE && item.CODE_NAME);
        postcodeCacheByCity.set(code, mapped);
        locCacheVersion.value += 1;
        return mapped;
    } catch (error) {
        console.error('[myRegisterAddressDisplay] Failed to load postcodes', { cityCode: code, error });
        postcodeCacheByCity.set(code, []);
        return [];
    }
}

function ensureBaseOptionsLoaded(): void {
    if (baseOptionsLoadStarted) return;
    baseOptionsLoadStarted = true;
    void loadBaseLocationOptions();
}

/** computed 재평가용 — 옵션/캐시 로드 후 표시 갱신 */
export function touchMyRegisterLocOptions(): number {
    ensureBaseOptionsLoaded();
    return locCacheVersion.value;
}

export type MyRegisterAddressDisplayContext = {
    countryOptions?: MyRegisterLocOption[];
    stateOptions?: MyRegisterLocOption[];
    cityOptions?: MyRegisterLocOption[];
    postcodeOptions?: MyRegisterLocOption[];
};

function normalizeMisalignedPostcode(data: Record<string, unknown>, prefix = ''): Record<string, unknown> {
    const p = prefix;
    const postcodeKeys = [
        `${p}POST_CODE`,
        `${p}POSTCODE`,
        `${p}POSTAL_CODE`,
        `${p}POSTCODE_CODE`,
    ];
    const stateKeys = [`${p}STATE`, `${p}STATE_CODE`];
    const postcodeRaw = pickFirstRaw(data, postcodeKeys);
    const stateRaw = pickFirstRaw(data, stateKeys);

    if (!isGuidLike(postcodeRaw) || !isNumericPostcodeLike(stateRaw)) {
        return data;
    }

    const next = { ...data };
    next[`${p}POST_CODE`] = stateRaw;
    next[`${p}POSTCODE`] = stateRaw;
    if (isGuidLike(next[`${p}STATE`]) || isNumericPostcodeLike(next[`${p}STATE`])) {
        next[`${p}STATE`] = '';
    }
    return next;
}

function buildLocationParts(
    data: Record<string, unknown>,
    prefix: '' | 'SERVICE_' | 'CONTACT_' | 'TFOR_' | 'TEE_',
    ctx: MyRegisterAddressDisplayContext,
): string[] {
    const p = prefix;
    const countryOpts = ctx.countryOptions ?? countryOptionsRef.value;
    const stateOpts = ctx.stateOptions ?? stateOptionsRef.value;

    const stateCode = pickFirstRaw(data, [
        `${p}STATE_CODE`,
        `${p}STATE`,
        `${p}CONTACT_STATE_CODE`,
    ]);
    const cityCode = pickFirstRaw(data, [`${p}CITY_CODE`, `${p}CITY`, `${p}CONTACT_CITY_CODE`]);
    const cityOpts =
        ctx.cityOptions ??
        (stateCode && !isGuidLike(stateCode) ? cityCacheByState.get(stateCode) : undefined) ??
        [];
    const postcodeOpts =
        ctx.postcodeOptions ??
        (cityCode && !isGuidLike(cityCode) ? postcodeCacheByCity.get(cityCode) : undefined) ??
        [];

    if (stateCode && isGuidLike(stateCode) === false && !cityCacheByState.has(stateCode)) {
        void ensureCitiesForState(stateCode);
    }
    if (cityCode && isGuidLike(cityCode)) {
        if (!cityNameCacheByGuid.has(cityCode)) {
            void ensureCityNameForGuid(cityCode);
        }
    } else if (cityCode && !postcodeCacheByCity.has(cityCode)) {
        void ensurePostcodesForCity(cityCode);
    }

    const postcodeTextKeys =
        p === 'SERVICE_'
            ? [
                  `${p}POST_CODE`,
                  `${p}POSTCODE`,
                  `${p}POSTAL_CODE`,
                  'MEMBER_SRV_POST',
                  'CONTACT_POST_CODE',
                  'CONTACT_POSTCODE',
                  'CONTACT_POSTAL_CODE',
              ]
            : [`${p}POST_CODE`, `${p}POSTCODE`, `${p}POSTAL_CODE`];
    const postcodeCodeKeys =
        p === 'SERVICE_'
            ? [
                  `${p}POSTCODE_CODE`,
                  'CONTACT_POSTCODE_CODE',
                  `${p}POST_CODE`,
                  `${p}POSTCODE`,
                  'MEMBER_SRV_POST',
              ]
            : [`${p}POSTCODE_CODE`, `${p}POST_CODE`, `${p}POSTCODE`];

    const cityTextKeys =
        p === 'SERVICE_'
            ? [`${p}CITY_TEXT`, 'CONTACT_CITY_TEXT', 'MEMBER_SRV_TOWN', `${p}CITY`, 'CONTACT_CITY']
            : [`${p}CITY_TEXT`, `${p}CITY`, 'CITY_TEXT', 'MEMBER_TOWN'];
    const cityCodeKeys =
        p === 'SERVICE_'
            ? [`${p}CITY_CODE`, 'CONTACT_CITY_CODE', `${p}CITY`, 'CONTACT_CITY', 'MEMBER_SRV_TOWN']
            : [`${p}CITY_CODE`, `${p}CITY`, 'CITY', 'MEMBER_TOWN'];

    const stateTextKeys =
        p === 'SERVICE_'
            ? [
                  `${p}STATE_TEXT`,
                  'CONTACT_STATE_TEXT',
                  'MEMBER_SRV_STATE_TEXT',
                  `${p}STATE`,
                  'CONTACT_STATE',
                  'MEMBER_SRV_STATE',
              ]
            : [`${p}STATE_TEXT`, `${p}STATE`, 'STATE_TEXT', 'MEMBER_STATE_TEXT', 'MEMBER_STATE'];
    const stateCodeKeys =
        p === 'SERVICE_'
            ? [
                  `${p}STATE_CODE`,
                  'CONTACT_STATE_CODE',
                  `${p}STATE`,
                  'CONTACT_STATE',
                  'MEMBER_SRV_STATE',
              ]
            : [`${p}STATE_CODE`, `${p}STATE`, 'STATE', 'MEMBER_STATE'];

    const countryTextKeys =
        p === 'SERVICE_'
            ? [`${p}COUNTRY_TEXT`, 'CONTACT_COUNTRY_TEXT', `${p}COUNTRY`, 'CONTACT_COUNTRY']
            : [`${p}COUNTRY_TEXT`, `${p}COUNTRY`, 'COUNTRY_TEXT', 'MEMBER_COUNTRYID_TEXT'];
    const countryCodeKeys =
        p === 'SERVICE_'
            ? [`${p}COUNTRY_CODE`, 'CONTACT_COUNTRY_CODE', `${p}COUNTRY`, 'CONTACT_COUNTRY']
            : [`${p}COUNTRY_CODE`, `${p}COUNTRY`, 'COUNTRY', 'MEMBER_COUNTRYID'];

    const countryResolved = resolveLocPart(data, countryTextKeys, countryCodeKeys, countryOpts);
    const countryRaw = pickFirstRaw(data, [...countryCodeKeys, ...countryTextKeys]);
    const countryDisplay = formatCountryForAddressDisplay(countryResolved);

    // Malaysia가 아니면 Street + Country만
    if (!shouldShowMyLocationFields(countryRaw || countryResolved, countryOpts)) {
        return [countryDisplay];
    }

    return [
        resolveLocPart(data, postcodeTextKeys, postcodeCodeKeys, postcodeOpts),
        resolveLocPart(data, cityTextKeys, cityCodeKeys, cityOpts),
        resolveLocPart(data, stateTextKeys, stateCodeKeys, stateOpts),
        countryDisplay,
    ];
}

function buildStreetParts(data: Record<string, unknown>, prefix: '' | 'SERVICE_' | 'CONTACT_' | 'TFOR_' | 'TEE_'): string[] {
    const p = prefix;
    if (p === 'SERVICE_') {
        return [
            pickFirstSanitized(data, [
                `${p}STREET01`,
                'MEMBER_SRV_ADD1',
                'CONTACT_STREET01',
                'CONTACT_ADDRESS_LINE_1',
            ]),
            pickFirstSanitized(data, [
                `${p}STREET02`,
                'MEMBER_SRV_ADD2',
                'CONTACT_STREET02',
                'CONTACT_ADDRESS_LINE_2',
            ]),
            pickFirstSanitized(data, [
                `${p}STREET03`,
                'MEMBER_SRV_ADD3',
                'CONTACT_STREET03',
                'CONTACT_ADDRESS_LINE_3',
            ]),
        ];
    }

    if (p === 'TFOR_' || p === 'TEE_') {
        return [
            pickFirstSanitized(data, [`${p}STREET01`, `${p}ADD1`, `${p}ADDRESS_LINE_1`]),
            pickFirstSanitized(data, [`${p}STREET02`, `${p}ADD2`, `${p}ADDRESS_LINE_2`]),
            pickFirstSanitized(data, [`${p}STREET03`, `${p}ADD3`, `${p}ADDRESS_LINE_3`]),
        ];
    }

    return [
        pickFirstSanitized(data, [`${p}STREET01`, 'MEMBER_ADD1', 'ADDRESS_LINE_1']),
        pickFirstSanitized(data, [`${p}STREET02`, 'MEMBER_ADD2', 'ADDRESS_LINE_2']),
        pickFirstSanitized(data, [`${p}STREET03`, 'MEMBER_ADD3', 'ADDRESS_LINE_3']),
    ];
}

function formatMyRegisterAddressInternal(
    data: Record<string, unknown> | null | undefined,
    prefix: '' | 'SERVICE_' | 'CONTACT_' | 'TFOR_' | 'TEE_',
    ctx: MyRegisterAddressDisplayContext = {},
): string {
    if (!data || typeof data !== 'object') return '';
    ensureBaseOptionsLoaded();
    const normalized = normalizeMisalignedPostcode(data, prefix);
    return joinAddressDisplayParts([
        ...buildStreetParts(normalized, prefix),
        ...buildLocationParts(normalized, prefix, ctx),
    ]);
}

export function formatMyRegisterRegisteredAddress(
    data: Record<string, unknown> | null | undefined,
    ctx: MyRegisterAddressDisplayContext = {},
): string {
    touchMyRegisterLocOptions();
    return formatMyRegisterAddressInternal(data, '', ctx);
}

export function formatMyRegisterServiceAddress(
    data: Record<string, unknown> | null | undefined,
    ctx: MyRegisterAddressDisplayContext = {},
): string {
    touchMyRegisterLocOptions();
    return formatMyRegisterAddressInternal(data, 'SERVICE_', ctx);
}

export function formatMyRegisterBusinessAddress(
    data: Record<string, unknown> | null | undefined,
    ctx: MyRegisterAddressDisplayContext = {},
): string {
    touchMyRegisterLocOptions();
    return formatMyRegisterAddressInternal(data, 'BUS_', ctx);
}

export function formatMyRegisterPrefixedAddress(
    data: Record<string, unknown> | null | undefined,
    prefix: 'TFOR_' | 'TEE_',
    ctx: MyRegisterAddressDisplayContext = {},
): string {
    touchMyRegisterLocOptions();
    return formatMyRegisterAddressInternal(data, prefix, ctx);
}

/** MY Register tree — 키 배열 기반 주소 (UUID/B15 필터) */
/** entity_my 프로필 주소 객체 → 한 줄 표시 (Register MY 탭 뷰) */
export function formatEntityProfileAddressLine(
    addr: Record<string, unknown> | null | undefined,
    ctx: MyRegisterAddressDisplayContext = {},
): string {
    if (!addr || typeof addr !== 'object') return '';
    return formatMyRegisterRegisteredAddress(
        {
            STREET01: addr.addressLine1,
            STREET02: addr.addressLine2,
            STREET03: addr.addressLine3,
            POST_CODE: addr.postcode ?? addr.post_code ?? addr.postalCode,
            CITY: addr.city,
            STATE: addr.state,
            COUNTRY: addr.country,
            COUNTRY_CODE: addr.countryCode,
            STATE_CODE: addr.stateCode,
            CITY_CODE: addr.cityCode,
            POSTCODE_CODE: addr.postcodeCode,
        },
        ctx,
    );
}

export function formatEntityProfileServiceAddressLine(
    addr: Record<string, unknown> | null | undefined,
    ctx: MyRegisterAddressDisplayContext = {},
): string {
    if (!addr || typeof addr !== 'object') return '';
    return formatMyRegisterServiceAddress(
        {
            SERVICE_STREET01: addr.addressLine1,
            SERVICE_STREET02: addr.addressLine2,
            SERVICE_STREET03: addr.addressLine3,
            SERVICE_POST_CODE: addr.postcode ?? addr.post_code ?? addr.postalCode,
            SERVICE_CITY: addr.city,
            SERVICE_STATE: addr.state,
            SERVICE_COUNTRY: addr.country,
            SERVICE_COUNTRY_CODE: addr.countryCode,
            SERVICE_STATE_CODE: addr.stateCode,
            SERVICE_CITY_CODE: addr.cityCode,
            SERVICE_POSTCODE_CODE: addr.postcodeCode,
        },
        ctx,
    );
}

export function formatEntityBranchAddressLine(branch: Record<string, unknown> | null | undefined): string {
    if (!branch || typeof branch !== 'object') return '';
    const addrRaw = String(branch.address ?? '').trim();
    const lineParts = addrRaw
        ? addrRaw.split(/\r\n|\n|\r/).map((x) => x.trim()).filter(Boolean)
        : [];
    const parts = [
        ...lineParts,
        String(branch.postCode ?? '').trim(),
        String(branch.town ?? '').trim(),
        String(branch.state ?? '').trim(),
        formatCountryForAddressDisplay(branch.countryName),
    ].filter((s) => s.length > 0);
    return joinAddressDisplayParts(parts);
}

export function formatEntityBranchContactLine(branch: Record<string, unknown> | null | undefined): string {
    if (!branch || typeof branch !== 'object') return '';
    const telParts = [String(branch.telNo0 ?? '').trim(), String(branch.telNo1 ?? '').trim()].filter(Boolean);
    const faxParts = [String(branch.faxNo0 ?? '').trim(), String(branch.faxNo1 ?? '').trim()].filter(Boolean);
    const parts: string[] = [];
    if (telParts.length) parts.push(telParts.join(' '));
    if (faxParts.length) parts.push(`Fax: ${faxParts.join(' ')}`);
    const email = String(branch.email ?? '').trim();
    if (email) parts.push(email);
    const website = String(branch.website ?? '').trim();
    if (website) parts.push(website);
    return parts.join(' · ');
}

export function formatEntityProfileContactExtras(addr: Record<string, unknown> | null | undefined): string {
    if (!addr || typeof addr !== 'object') return '';
    const parts: string[] = [];
    const tel = String(addr.tel ?? '').trim();
    if (tel) parts.push(tel);
    const fax = String(addr.fax ?? '').trim();
    if (fax) parts.push(`Fax: ${fax}`);
    const email = String(addr.email ?? '').trim();
    if (email) parts.push(email);
    const website = String(addr.website ?? '').trim();
    if (website) parts.push(website);
    return parts.join(' · ');
}

export function formatMyRegisterAddressFromKeys(
    record: Record<string, unknown> | null | undefined,
    keys: string[],
    ctx: MyRegisterAddressDisplayContext = {},
): string {
    if (!record || typeof record !== 'object') return '';
    touchMyRegisterLocOptions();

    const countryOpts = ctx.countryOptions ?? countryOptionsRef.value;
    const stateOpts = ctx.stateOptions ?? stateOptionsRef.value;

    const parts = keys
        .map((key) => {
            const raw = String(record[key] ?? '').trim();
            if (!raw) return '';
            if (key.includes('COUNTRY')) return formatCountryForAddressDisplay(lookupLocName(raw, countryOpts));
            if (key.includes('STATE')) return lookupLocName(raw, stateOpts);
            return sanitizeDisplayPart(raw);
        })
        .filter(Boolean);

    return joinAddressDisplayParts(parts);
}

ensureBaseOptionsLoaded();
